import Hls from 'hls.js';
import { adCutAnchor, alignToChunkStart, cutDueWithoutFrames, videoStartFromFrame, } from '../../core/chunk-align.js';
import { FRAME_DURATION_SEC } from '../../core/types.js';
/**
 * hls.js 기반 `PlayerAdapter`. `<video>` 두 개를 번갈아 쓰는 더블 버퍼링이다.
 *
 * 이 파일의 모든 타이밍 선택은 M0 실측 근거가 있다 (`docs/spike-result.md`).
 * 임의로 바꾸면 무갭이 깨지고, 깨진 것을 알아채기 어렵다 — `TransitionMetrics.path` 가
 * 유일한 관찰 창이다.
 */
/** hls.js 는 1.6.16 으로 고정한다 (M7 이 M0 수치와 비교하는 전제). */
const HLS_CONFIG = {
    enableWorker: true,
    lowLatencyMode: false,
    // 기본값(30s)이면 30초짜리 자산 전체를 즉시 버퍼링해 프리로드 여유가 인위적으로 부풀려진다.
    // 스파이크와 같은 값을 쓴다 — 다르면 M7 비교가 성립하지 않는다.
    maxBufferLength: 10,
    maxMaxBufferLength: 20,
    backBufferLength: 10,
    autoStartLoad: false,
    startFragPrefetch: true,
};
const ARM_TIMEOUT_MS = 15_000;
/**
 * 치명적 오류를 되살려 보는 횟수. **상한이 있어야 한다** — 무한 재시도는 오류 화면이
 * 아니라 「조용히 안 나온다」가 되고, 그쪽이 더 나쁘다. 근거는 `arm()` 의 ERROR 핸들러.
 */
const FATAL_NET_RETRIES = 2;
const FATAL_MEDIA_RETRIES = 1;
/**
 * 탐색이 끝난 프레임을 몇 장까지 기다리는가 (v27/TG16). 30fps 에서 8장 ≈ 0.27초다.
 * **다 쓰면 0 으로 물러난다** — 못 잡고 기다리는 것보다 한 프레임 일찍 자르는 편이 낫다
 * (늦으면 회차 끝에서 채널이 선다).
 */
const SEEK_SETTLE_FRAMES = 8;
/**
 * ★ v33-e. **적재가 프레임을 기다리는 상한**(ms) — `native-hls-player.ts` 와 같은 값이다
 * (2026-09-23 · `docs/68` §2.0-g).
 *
 * **보이지 않는 창에서 Chrome 은 프레임 콜백을 멈춘다.** 녹화물의 적재는 「탐색이 끝난 첫
 * 프레임」을 기다리므로, 그 창에서는 모든 적재가 `ARM_TIMEOUT_MS`(15초)를 다 태우고 죽었다 —
 * 라이브가 끝날 때의 복귀 적재도 그랬고, 그래서 **엉뚱한 자리(46.9 · 컷은 74.77)로 돌아와
 * 3초 뒤 멈췄다**(AWS · 로컬 하네스 `--hidden` 실측). 아이폰 어댑터에는 처음부터 있던
 * 유예다 — **없는 쪽이 웹이었다.**
 *
 * 유예로 들어오면 `currentTime` 으로 대신한다 — 그 값은 탐색 목표와 같아 「위치 0」을 0 으로
 * 잡고, 그것은 한 프레임 **일찍** 자르는 안전한 방향이다(아래 `settleStart` 의 「0 으로
 * 물러난다」와 같은 값). 보이는 창에서는 첫 프레임이 수십 ms 안에 와서 이 유예가 쓰이지 않는다.
 */
const FRAME_PROOF_GRACE_MS = 400;
/** 워치독: 마지막 프레임 이후 이만큼 새 프레임이 없으면 스왑한다. 1.5 프레임. */
const WATCHDOG_MS = 1.5 * FRAME_DURATION_SEC * 1000;
export class HlsPlayerAdapter {
    buffers;
    front = 0;
    tail = null;
    /** 스왑·무장 세대. 오래된 프레임 콜백과 워치독을 무효화한다. */
    gen = 0;
    swapping = false;
    /**
     * ★ **엘리먼트에 붙인 리스너를 뗄 수 있게 한다** (v19).
     *
     * 전에는 엘리먼트가 어댑터와 함께 죽어서 뗄 필요가 없었다. **입양하면 엘리먼트가
     * 살아남으므로** 떼지 않으면 전환마다 쌓이고, 그것이 절대 규칙 27 이 경고하는
     * 「세 번째 전환쯤부터 `start` 가 두 번씩 나간다」다.
     */
    listeners = new AbortController();
    /** 엘리먼트를 남에게 받았는가. `destroy()` 가 뗄지 되돌릴지를 이 값이 가른다. */
    adopted;
    vsyncMs = 1000 / 60;
    timeUpdateCb = null;
    errorCb = null;
    transitionCb = null;
    constructor(opts) {
        this.adopted = opts.buffers !== undefined;
        this.buffers = [
            this.createBuffer(opts.container, opts.buffers?.[0]),
            this.createBuffer(opts.container, opts.buffers?.[1]),
        ];
        this.reveal(0);
        void this.measureVsync();
    }
    /** 보이는 버퍼. */
    get frontBuf() {
        return this.buffers[this.front];
    }
    /** 대기 버퍼. `opacity: 0` 이고 음소거 상태다. */
    get backBuf() {
        return this.buffers[this.front === 0 ? 1 : 0];
    }
    get backIndex() {
        return this.front === 0 ? 1 : 0;
    }
    // ── 적재·재생 ───────────────────────────────────────────────────────────
    async load(url, startAtSec) {
        const b = this.frontBuf;
        await this.arm(b, url, startAtSec);
        this.reveal(this.front);
    }
    async play() {
        const b = this.frontBuf;
        b.el.muted = false;
        await b.el.play();
        b.playing = true;
        this.startWatch(b);
    }
    pause() {
        const b = this.frontBuf;
        b.el.pause();
        b.playing = false;
        // v35. 꼬리 창에서 멈추면 워치독이 정지 중에 스왑을 터뜨린다 — 근거는 `rewindPreroll`.
        this.rewindPreroll();
    }
    /** v35. 앞 버퍼의 위치만 옮긴다. 상한은 코어가 이미 걸었다(절대 규칙 91) */
    seek(sec) {
        this.rewindPreroll();
        this.frontBuf.el.currentTime = sec;
    }
    /**
     * ★ v35. **꼬리 프리롤을 「아직 시작 전」으로 되돌린다** (`docs/71` §2.2 · 절대 규칙 92).
     *
     * 꼬리 `prerollSec` 안에서는 대기 버퍼가 음소거로 이미 돌고 워치독이 무장된다. 그 창에서
     * ① 정지하면 프레임이 멈춰 워치독이 **정지 중에 스왑을 터뜨리고** ② 뒤로 가면 대기 버퍼가
     * 그동안 계속 돌아 **다음 것이 몇 초 들어간 데서 시작한다.** 둘 다 증상이 원인을 말하지
     * 않는다(「정지가 안 먹는다」 · 「다음 회차 첫 장면이 없다」).
     *
     * 컷 위치(`swapAt`·`prerollAt`)는 mediaTime 축이라 그대로 두고, 재생이 다시 그 자리에
     * 닿으면 프레임 체인이 프리롤을 다시 건다.
     */
    rewindPreroll() {
        const tail = this.tail;
        if (tail === null || !tail.prerolled)
            return;
        if (tail.watchdog !== null) {
            clearTimeout(tail.watchdog);
            tail.watchdog = null;
        }
        tail.prerolled = false;
        tail.prerollStartedAt = null;
        const back = this.backBuf;
        back.el.pause();
        back.playing = false;
        if (tail.backStartAt !== null)
            back.el.currentTime = tail.backStartAt;
        tail.backStartAt = null;
    }
    stop() {
        this.gen += 1;
        this.clearTail();
        /*
         * **`swapping` 을 여기서 내린다** (v19). 스왑의 지연 정리가 그것을 내려 주는데, 위에서
         * `gen` 을 올린 순간 그 콜백은 **가드에 걸려 돌아간다** — 내려 주는 자리가 사라지므로
         * 여기서 내리지 않으면 `performSwap` 이 영원히 `false` 를 돌려주고 **채널이 멈춘다.**
         * `TASKS-V12` 가 어댑터 재사용에서 경계하던 「굳는다」가 정확히 이 모양이다.
         */
        this.swapping = false;
        for (const b of this.buffers) {
            b.el.pause();
            b.playing = false;
            b.watching = false;
            this.destroyHls(b);
        }
    }
    /**
     * 대기 버퍼에 적재하고 **첫 프레임이 합성되는 것까지** 확인한다.
     * 대기 버퍼는 `opacity: 0` 이고 음소거 상태로 둔다.
     */
    async prepareNext(url, startAtSec) {
        const b = this.backBuf;
        if (b.url === url && b.ready)
            return;
        /*
         * ★ **스왑을 기다리는 적재를 앞지르지 않는다** (`ArmInFlight` 의 ★).
         *
         * 이 배경 적재가 노리는 버퍼를 `swapNow()` 가 이미 적재 중이면, 그 버퍼는 **곧 앞으로
         * 나갈 자리**다 — 거기에 「다음다음 회차」를 얹는 것은 경합 이전에 뜻이 틀렸다.
         * 비켜서면 코어가 스왑 뒤 `armNext()` 로 **옳은 버퍼에** 다시 부른다.
         *
         * 비켜서지 않으면 냉시작 스킵이 적재를 통째로 잃고 처음부터 다시 받는다 —
         * 그것이 `docs/48` §5.9 의 15초를 넓힌 자리다.
         */
        if (b.arming?.forSwap === true)
            return;
        b.el.muted = true;
        await this.arm(b, url, startAtSec);
    }
    // ── 꼬리 스왑 ───────────────────────────────────────────────────────────
    /**
     * v21. **`opts.atSec` 이 있으면 영상 끝이 아니라 그 초에서 자른다** (미드롤).
     *
     * ── ★ 재무장 규약 둘 ──────────────────────────────────────────
     *
     * 미드롤은 `armTailSwap()` 을 **재생 도중에 다시 부르는 최초의 경우**다. 그전에는
     * 회차가 시작될 때 한 번뿐이었다.
     *
     * ① **재무장 전에 앞선 무장을 끊는다** — 워치독 타이머가 남아 있으면 새 무장을 밀어낸다.
     * ② **앵커를 구하지 못하면 무장하지 않고 조용히 돌아간다.** `onAbandoned` 로 흘리면
     *    코어의 `recoverByLoad` 가 **회차를 통째로 넘긴다** — 광고 하나를 못 트는 대가로
     *    드라마 한 편을 건너뛰는 셈이다. 자연 전환의 무장(=`atSec` 없음)에서만 그 신호가
     *    옳다: 그때는 진짜로 회차 끝을 모르는 것이다.
     */
    armTailSwap(opts) {
        // ① 규약 — 앞선 무장을 먼저 끊는다. `gen` 은 올리지 않는다(프레임 체인은 살아야 한다).
        this.clearTail();
        const front = this.frontBuf;
        const lastFrame = lastFrameMediaTime(front);
        // ── 미드롤: 컷 지점을 청크 경계로 내려 mediaTime 으로 옮긴다 ──
        if (opts.atSec !== undefined) {
            const cut = adCutAnchor(front.segmentDurations, front.videoStart, front.videoSpan, opts.atSec);
            if (cut === null) {
                // ② 규약 — **조용히 거른다.** 브레이크 하나를 잃고 회차는 그대로 간다.
                return;
            }
            this.tail = {
                opts,
                prerollAt: cut.mediaTime - opts.prerollSec,
                swapAt: cut.mediaTime,
                cutAtSec: cut.cutAtSec,
                prerolled: false,
                prerollStartedAt: null,
                backStartAt: null,
                watchdog: null,
            };
            this.startWatch(front);
            return;
        }
        // 예측 스왑을 쓸 수 없을 때만 `duration` 으로 폴백한다. `duration` 은 비디오 끝과 오디오
        // 끝 중 큰 쪽이므로 프리롤이 **일찍** 발화하는 안전한 방향이고, 스왑 판정에는 쓰지 않는다
        // (오디오가 16.67ms 넘게 길면 조건이 한 번도 참이 되지 않아 채널이 멈춘다).
        const fallbackEnd = Number.isFinite(front.el.duration)
            ? front.el.duration - FRAME_DURATION_SEC
            : null;
        const anchor = lastFrame ?? fallbackEnd;
        if (anchor === null) {
            opts.onAbandoned('비디오 끝을 추정할 수 없다 (duration 도 EXTINF 도 없음)');
            return;
        }
        this.tail = {
            opts,
            prerollAt: anchor - opts.prerollSec,
            swapAt: lastFrame,
            cutAtSec: undefined,
            prerolled: false,
            prerollStartedAt: null,
            backStartAt: null,
            watchdog: null,
        };
        this.startWatch(front);
    }
    /**
     * 정책상 초 → **청크 경계로 내린 mediaTime**.
     *
     * 돌려주는 것이 둘이다 — 프레임 체인이 비교할 `mediaTime` 과, 코어에 되돌려 줄
     * **콘텐츠 축의 초**(`cutAtSec`). 둘이 `videoStart` 만큼 어긋나 있고, 그 값을 아는 것은
     * 어댑터뿐이다.
     *
     * **`segmentDurations` 나 `videoStart` 가 없으면 `null`** — 아직 매니페스트가 오지
     * 않았거나 첫 프레임이 합성되지 않았다. 그때는 무장하지 않는다(위 규약 ②).
     */
    cancelTailSwap() {
        this.gen += 1;
        this.clearTail();
    }
    async swapNow(trigger) {
        this.cancelTailSwap();
        const requestedAt = performance.now();
        // 자연 스왑이 진행 중이면 그것이 끝나기를 기다린다. 클릭이 전환과 같은 순간에
        // 떨어지는 좁은 창이지만, 기다리지 않으면 `performSwap` 이 재진입 가드에 걸려
        // **화면은 그대로인데 코어는 인덱스를 올린다** — 이후 화면과 상품 정보가 계속 어긋난다.
        await this.settleSwap();
        // A 의 마지막 **표시된** 프레임. 프레임 체인이 매 프레임 갱신해 둔 값이다.
        // 스파이크의 S3 가 `lastA.edt` 로 쓴 것과 같은 값이고, 이것을 넘기지 않으면 스킵 경로의
        // `tFreezeMs` 가 영원히 `null` 이 되어 M0 의 S3(83.5ms)와 대조할 수 없다.
        const aLastEdt = this.frontBuf.lastFrameEdt;
        const back = this.backBuf;
        let path;
        if (back.ready) {
            // 꼬리 프리롤 창 안에서 스킵이 들어왔으면 이미 재생·합성 중이다. 공짜로 무갭이 된다.
            path = back.playing ? 'preroll' : 'no-preroll';
        }
        else {
            if (!back.url)
                throw new Error('대기 버퍼에 URL 이 없다. prepareNext 가 필요하다.');
            // **스왑이 기다리는 적재다** — 배경 적재가 이것을 앞지르지 못하게 표시하고,
            // 같은 URL 이 이미 돌고 있으면 다시 받지 않고 올라탄다 (`ArmInFlight` 의 ★).
            await this.arm(back, back.url, undefined, true);
            path = 'cold';
        }
        if (!this.performSwap(path, trigger, aLastEdt, requestedAt)) {
            throw new Error('스왑이 재진입 가드에 걸렸다.');
        }
    }
    /**
     * `swapping` 이 풀리기를 기다린다. 자연 스왑은 `performSwap` 의 `setTimeout(0)` 정리가
     * 끝나면 풀리므로 한 태스크면 충분하지만, 상한을 둬서 어떤 이유로든 풀리지 않을 때
     * 클릭이 영영 삼켜지지 않게 한다.
     */
    async settleSwap() {
        for (let i = 0; this.swapping && i < 10; i += 1) {
            await new Promise((resolve) => setTimeout(resolve, 0));
        }
    }
    // ── 콜백 등록 ───────────────────────────────────────────────────────────
    onTimeUpdate(cb) {
        this.timeUpdateCb = cb;
    }
    onError(cb) {
        this.errorCb = cb;
    }
    onTransition(cb) {
        this.transitionCb = cb;
    }
    destroy() {
        this.stop();
        // **리스너를 먼저 뗀다.** 입양한 엘리먼트는 살아남으므로 떼지 않으면 전환마다 쌓인다.
        this.listeners.abort();
        for (const b of this.buffers) {
            if (this.adopted) {
                // **되돌리기만 한다.** `stop()` 이 이미 `pause()` 와 소스 정리를 했고, 남은 것은
                // 다음 어댑터가 깨끗한 상태를 받게 하는 것뿐이다 — 생성자의 `reveal(0)` 이
                // `opacity`/`zIndex` 를 다시 정하므로 여기서는 소리만 되돌린다.
                b.el.muted = true;
            }
            else {
                b.el.remove();
            }
        }
    }
    /**
     * 디버그 패널용 상태 스냅샷. `docs/06-frontend-spec.md` 가 요구하는 "프리로드 완료 여부"가
     * 이것이다.
     *
     * **`PlayerAdapter` 계약에 넣지 않는다.** 계약은 플랫폼 무관해야 하고, 디버그 항목이
     * 거기 있으면 iOS/Android 어댑터가 웹의 내부 사정을 흉내내야 한다. 패널은 구체 클래스를
     * 직접 들고 있으므로 이대로 충분하다.
     *
     * **읽기 전용이고 부작용이 없다.** 패널이 이 값을 매 프레임 폴링하면 그 자체가 측정
     * 교란이므로 호출 주기는 호출자가 낮게 잡아야 한다.
     */
    debugSnapshot() {
        const front = this.frontBuf;
        const back = this.backBuf;
        const duration = Number.isFinite(front.el.duration) ? front.el.duration : null;
        return {
            frontIndex: this.front,
            nextReady: back.ready,
            nextPlaying: back.playing,
            nextUrl: back.url,
            tailArmed: this.tail !== null,
            prerolled: this.tail?.prerolled ?? false,
            armedAtSec: this.tail?.cutAtSec ?? null,
            videoStart: front.videoStart,
            videoSpan: front.videoSpan,
            duration,
            vsyncMs: this.vsyncMs,
            liveWallMs: front.hls?.playingDate?.getTime() ?? null,
        };
    }
    // ── 내부 ───────────────────────────────────────────────────────────────
    createBuffer(container, existing) {
        // **입양하면 만들지 않는다** — `native-hls-player.ts` 의 같은 자리와 짝이다.
        const el = existing ?? document.createElement('video');
        el.playsInline = true;
        el.preload = 'auto';
        el.muted = true;
        // **`opacity` 로만 숨긴다.** `display:none`·`visibility:hidden`·화면 밖 배치는 디코드는
        // 하지만 합성하지 않아(E0: 디코드 88프레임 중 표시 2프레임) 노출 순간 파이프라인 전체를
        // 물게 된다. 여기를 바꾸면 무갭이 원리적으로 불가능해진다.
        el.style.cssText =
            'position:absolute;top:0;left:0;width:100%;height:100%;' +
                'object-fit:contain;background:#000;opacity:0;z-index:1;';
        el.addEventListener('timeupdate', () => {
            if (this.frontBuf.el !== el)
                return;
            this.timeUpdateCb?.(el.currentTime);
            // v33-e. 프레임이 끊긴 창에서는 컷을 여기서 판정한다 — 근거는 `cutDueWithoutFrames`.
            this.cutWithoutFrames(el);
        }, { signal: this.listeners.signal });
        el.addEventListener('error', () => {
            this.errorCb?.(el.error);
        }, { signal: this.listeners.signal });
        // **폴백 3층 — 최후 백스톱.** 예측 스왑과 워치독이 모두 불발했을 때 여기서라도 넘긴다.
        // S1 이므로 약 150ms 프리즈지만 **채널은 계속된다** — 무갭 실패보다 정지가 훨씬 나쁘다.
        // `path: 'ended'` 로 보고되므로 계측에서 이 층이 쓰였다는 사실이 드러난다.
        //
        // 코어에 `onEnded` 훅으로 노출하지 않는 이유는 `interfaces.ts` 에 적혀 있다 —
        // 그 훅이 있으면 언젠가 거기서 전환하게 되고, 그것이 정확히 S1(무갭률 0%)이다.
        el.addEventListener('ended', () => {
            if (this.frontBuf.el !== el || this.tail === null || this.swapping)
                return;
            this.performSwap('ended', 'natural', this.frontBuf.lastFrameEdt, null);
        }, { signal: this.listeners.signal });
        container.appendChild(el);
        return {
            el,
            hls: null,
            url: null,
            ready: false,
            playing: false,
            live: false,
            videoStart: null,
            segmentDurations: null,
            abortArm: null,
            arming: null,
            videoSpan: null,
            frames: 0,
            lastFrameEdt: null,
            lastFrameAt: null,
            watching: false,
            watchGen: 0,
        };
    }
    /**
     * 버퍼를 비운다. **이 버퍼의 프레임 체인도 함께 끊는다** — 세대를 올려 예약된 콜백이
     * 다음 발화에서 스스로 반환하게 한다. 여기서 끊지 않으면 `hls.destroy()` 뒤에도
     * 체인이 남아 `watching` 이 true 로 굳고, 그 버퍼를 다시 쓸 때 `startWatch` 가
     * 가드에 걸려 조용히 아무 체인도 돌지 않는다.
     */
    destroyHls(b) {
        // ★ **적재 중이던 약속을 여기서 끝낸다** — 근거는 `Buffer.abortArm`. 아래에서 hls 를
        // 죽이면 그 약속에는 `loadeddata` 도 `ERROR` 도 다시 오지 않으므로, 끝내 주지 않으면
        // 15초 뒤에야 스스로 거절한다.
        const abort = b.abortArm;
        b.abortArm = null;
        b.arming = null;
        abort?.(new Error(`적재가 앞질러졌다: ${b.url ?? ''}`));
        if (b.hls) {
            b.hls.destroy();
            b.hls = null;
        }
        b.watchGen += 1;
        b.watching = false;
        b.ready = false;
        // v33. **다음 적재가 VOD 일 수 있다** — 비우지 않으면 라이브를 튼 버퍼가 그 뒤로
        // 영원히 「라이브」로 남아 **꼬리 스왑을 못 하고** 회차가 끝에서 멈춘다.
        b.live = false;
        b.videoStart = null;
        b.segmentDurations = null;
        b.videoSpan = null;
        b.frames = 0;
        b.lastFrameEdt = null;
        b.lastFrameAt = null;
    }
    /**
     * 적재하고 첫 프레임이 **합성되는 것까지** 확인한다 (스파이크 armedProof).
     * `canplaythrough` 는 네트워크 처리량 예측일 뿐 준비 완료의 증거가 아니다. 프레임을 한 번도
     * 합성한 적 없는 엘리먼트를 노출하면 그때는 실제로 검은 화면이 뜬다.
     */
    /**
     * `startAtSec` 이 있으면 **그 초를 포함하는 청크의 시작**부터 적재한다
     * (v16, 특허 청구항 1 (c)·7).
     *
     * 정렬은 `LEVEL_LOADED` 에서 한다 — 세그먼트 길이 목록(`details.fragments`)이 그때
     * 처음 생긴다. **`#EXTINF` 를 직접 파싱하지 않는다**: hls.js 가 이미 파싱해 둔 것을 읽고,
     * 그래야 매니페스트를 두 번 받지 않는다.
     */
    arm(b, url, startAtSec, forSwap = false) {
        const inFlight = b.arming;
        if (inFlight !== null && inFlight.url === url && inFlight.startAtSec === startAtSec) {
            // 같은 적재가 이미 돈다 — 다시 받지 않고 올라탄다 (`ArmInFlight` 의 ★).
            if (forSwap)
                inFlight.forSwap = true;
            return inFlight.promise;
        }
        // `armFresh` 의 첫 줄이 `destroyHls()` 라 **여기서 옛 `arming` 이 지워진다.**
        // 그래서 새 것을 그 **뒤에** 건다 — 앞에 걸면 스스로 지운다.
        const promise = this.armFresh(b, url, startAtSec);
        const entry = { url, startAtSec, forSwap, promise };
        b.arming = entry;
        const clear = () => {
            if (b.arming === entry)
                b.arming = null;
        };
        // 성공·실패 둘 다에 처리기를 달아 **처리되지 않은 거절을 만들지 않는다.**
        // 원래 약속은 그대로 부르는 쪽으로 간다.
        void promise.then(clear, clear);
        return promise;
    }
    armFresh(b, url, startAtSec) {
        this.destroyHls(b);
        b.url = url;
        b.playing = false;
        return new Promise((resolve, reject) => {
            const hls = new Hls({ ...HLS_CONFIG });
            b.hls = hls;
            let settled = false;
            /** 프레임 대기의 유예 — 근거는 `FRAME_PROOF_GRACE_MS` */
            let grace = null;
            const timer = setTimeout(() => finish(new Error(`적재 시간 초과: ${url}`)), ARM_TIMEOUT_MS);
            const finish = (err) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(timer);
                if (grace !== null)
                    clearTimeout(grace);
                // 내가 건 손잡이일 때만 뗀다 — 이미 다음 `arm()` 이 자기 것을 걸어 뒀을 수 있다.
                if (b.abortArm === finish)
                    b.abortArm = null;
                b.el.removeEventListener('loadeddata', onLoaded);
                if (err)
                    reject(err);
                else
                    resolve();
            };
            // `destroyHls()` 가 이것을 부른다 (`Buffer.abortArm` 의 ★).
            b.abortArm = finish;
            /*
             * ★ **치명적 오류를 한 번은 되살린다** (2026-08-27).
             *
             * 지금까지는 `data.fatal` 이면 곧장 거절이었다. 그런데 hls.js 가 `fatal` 을 다는 것은
             * **자기 내부 재시도를 이미 다 쓴 뒤**이고, 그 위에서 되살리는 방법을 라이브러리가
             * 따로 준다 — 네트워크는 `startLoad()`, 미디어는 `recoverMediaError()`.
             * 그것을 쓰지 않으면 **데모 현장의 망이 잠깐 흔들린 것이 그대로 오류 스크림**이다.
             *
             * **`swapAudioCodec()` 은 쓰지 않는다** — 인코딩 규격이 고정값이라(절대 규칙 4)
             * 오디오 코덱이 바뀔 일이 없고, 부르면 멀쩡한 버퍼를 한 번 더 흔든다.
             *
             * **상한을 둔다.** 무한 재시도는 「조용히 안 나온다」가 되고, 그것이 오류 화면보다
             * 나쁘다. 적재 중이면 `ARM_TIMEOUT_MS` 가 그 위에 또 한 겹이다.
             *
             * ── 적재가 끝난 **뒤**의 치명적 오류 ─────────────────────────
             *
             * 그때는 `settled` 라 지금까지 **아무 일도 일어나지 않았다.** 프레임이 멈추므로
             * 프레임 체인도 워치독도 함께 멈추고, 꼬리 스왑이 영영 발화하지 않는다 —
             * **채널이 조용히 언다.** 되살리지 못하면 코어로 올려 시청자에게 「다시 시도」를
             * 준다. 언 화면보다 낫다.
             *
             * **대기 버퍼면 올리지 않는다** — 화면에 없는 버퍼 때문에 오류 스크림이 뜨면
             * 규칙 51 이 광고에 대해 막으려던 것과 같은 일이 회차에서 일어난다. 준비 안 됨으로
             * 강등만 하면 꼬리 스왑이 `cold` 로 내려가 스스로 다시 적재한다.
             */
            let netRetries = 0;
            let mediaRetries = 0;
            hls.on(Hls.Events.ERROR, (_evt, data) => {
                if (!data.fatal)
                    return;
                if (data.type === Hls.ErrorTypes.NETWORK_ERROR && netRetries < FATAL_NET_RETRIES) {
                    netRetries += 1;
                    hls.startLoad();
                    return;
                }
                if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < FATAL_MEDIA_RETRIES) {
                    mediaRetries += 1;
                    hls.recoverMediaError();
                    return;
                }
                const err = new Error(`hls 치명적 오류: ${data.type} / ${data.details}`);
                if (!settled) {
                    finish(err);
                    return;
                }
                if (this.frontBuf !== b) {
                    b.ready = false;
                    return;
                }
                this.errorCb?.(err);
            });
            // **비디오 스팬을 여기서 얻는다.** EXTINF 합은 비디오 프레임 수 / 30 이고 오디오 꼬리를
            // 포함하지 않는다. `duration`(= max(비디오끝, 오디오끝))으로 마지막 프레임을 판정하면
            // 오디오가 16.67ms 넘게 긴 자산에서 채널이 멈춘다 — 「부채 정산」 3절.
            // 정렬 결과. `LEVEL_LOADED` 가 채운다 — 그 전에 `loadeddata` 가 오는 일은 없다
            // (프래그먼트를 받으려면 미디어 플레이리스트가 먼저 와야 한다).
            let alignedStartSec = 0;
            hls.on(Hls.Events.LEVEL_LOADED, (_evt, data) => {
                /*
                 * ★★ v33. **라이브면 세 값을 비워 둔다** (`docs/67` §7.6 ② · 절대 규칙 84).
                 *
                 * 라이브 매니페스트의 `totalduration` 과 `fragments` 는 **창(약 20초)의 것**이다.
                 * 그대로 채우면 `lastFrameMediaTime` 이 「20초 뒤가 영상 끝」이라고 말하고,
                 * `adCutAnchor` 도 그 창 안에서 컷 지점을 구한다 — **붙자마자 꼬리 스왑이 터진다**
                 * (TG16 이 `videoStart` 에서 겪은 그 모양). 비워 두면 둘 다 `null` 을 내므로
                 * **라이브 버퍼에는 원리적으로 무장할 수 없다.**
                 */
                b.live = data.details.live;
                if (b.live)
                    return;
                const total = data.details.totalduration;
                if (Number.isFinite(total) && total > 0)
                    b.videoSpan = total;
                // v21. **미드롤이 이 목록으로 컷 지점을 구한다.** 이어보기와 같은 출처다 —
                // 정렬 구현이 하나뿐인 것이 절대 규칙 47 이 지키는 것 전부다.
                const durations = data.details.fragments.map((f) => f.duration);
                b.segmentDurations = durations;
                if (startAtSec !== undefined && startAtSec > 0) {
                    alignedStartSec = alignToChunkStart(durations, startAtSec).startSec;
                }
            });
            /*
             * ★★ v27/TG16 — **탐색이 끝난 프레임에서만 눈금을 잡는다.**
             *
             * `videoStart` 는 「위치 0 에 해당하는 mediaTime」이지 **첫 프레임의 것이 아니다.**
             * 둘은 처음부터 재생할 때만 같다(그때 값은 AAC 프라이밍 ≈0.021).
             *
             * 전에는 `currentTime` 을 걸고 **바로 다음 프레임**을 그 값으로 삼았다. 그런데
             * **탐색은 비동기라** 그 프레임이 탐색 **전**의 것일 수 있다 — 실측에서
             * `mediaTime 0.021 / alignedStartSec 150` 이 잡혀 `videoStart` 가 **−149.98** 이
             * 됐고, 그러면 회차 끝(`lastFrameMediaTime`)이 **이어보기 지점 그 자리**로
             * 내려앉아 **붙자마자 꼬리 스왑이 터진다**(`chunk-align.ts` 의 그 ★).
             *
             * **고침은 프레임을 거르는 것 하나다.** 이른 프레임이면 순수 함수가 `null` 을
             * 내고 **한 장 더 기다린다.** 프레임 콜백은 **실제로 합성된 프레임에만** 오므로
             * 다음 장은 탐색이 끝난 뒤의 것이고, 대개 한 번이면 끝난다.
             *
             * ★ **`seeked` 를 기다리는 판으로 만들었다가 되돌렸다** (2026-09-04). 그 이벤트를
             * 기다리는 동안 프레임을 한 장도 청하지 않으니 `finish()` 가 영영 안 불렸고,
             * **적재가 15초 시간 초과로 죽어 재생이 아예 서지 않았다**(`idx: -1` · `start`
             * 0건 · 컨트롤 바 없음). 프레임 쪽이 스스로 그 순서를 지킨다.
             *
             * 끝내 못 받으면 **0 으로 물러난다** — 프라이밍(≈0.021)만큼 한 프레임 **일찍**
             * 자르는 안전한 방향이다(늦으면 회차 끝에서 채널이 선다).
             */
            const settleStart = (mediaTime, tries) => {
                // 프레임과 유예가 **둘 다** 올 수 있다 — 먼저 온 쪽만 쓴다.
                if (settled)
                    return;
                if (grace !== null) {
                    clearTimeout(grace);
                    grace = null;
                }
                /*
                 * ★ v33. **라이브는 눈금을 잡지 않는다.** 라이브의 `mediaTime` 은 창의 절대
                 * 좌표(수만 초)라 「위치 0」이 뜻을 갖지 않는다. `null` 로 두면 `adCutAnchor` 가
                 * `null` 을 내 무장이 거부되고, 그것이 위 ★★ 의 짝이다.
                 */
                if (b.live) {
                    b.videoStart = null;
                    b.ready = true;
                    finish();
                    return;
                }
                const start = videoStartFromFrame(mediaTime, alignedStartSec);
                if (start === null && tries < SEEK_SETTLE_FRAMES) {
                    // **유예를 되건다** — 아이폰 어댑터가 v33-c 에서 값을 치른 그 자리(두 번째 대기부터 굳었다).
                    grace = setTimeout(() => {
                        settleStart(b.el.currentTime, SEEK_SETTLE_FRAMES);
                    }, FRAME_PROOF_GRACE_MS);
                    requestVideoFrame(b.el, (_now, meta) => {
                        settleStart(meta.mediaTime, tries + 1);
                    });
                    return;
                }
                b.videoStart = start ?? 0;
                b.ready = true;
                finish();
            };
            const onLoaded = () => {
                if (b.el.readyState < 2)
                    return;
                /*
                 * ★★★★ v33. **라이브는 프레임을 기다리지 않는다 — 기다릴 프레임이 없다.**
                 *
                 * 아래 갈래는 `currentTime` 을 걸어 **탐색을 일으키고**, 그 탐색이 내놓는 프레임을
                 * `requestVideoFrame` 이 받는다. 라이브에는 그 대입이 없으므로(바로 아래 ★★★)
                 * **아무 일도 일어나지 않고**, 멈춰 있는 `<video>` 는 새 프레임을 합성하지 않는다.
                 * 그러면 `settleStart` 가 영영 안 돌아 `finish()` 도 안 돌고, **이 약속이 15초
                 * 뒤 `ARM_TIMEOUT_MS` 로 거절된다** — `enterLiveNow` 의 `await load()` 가 거기
                 * 서 있으므로 **그 뒤의 `play()` 에 닿지 못한다.**
                 *
                 * 증상은 **「채널에 들어가니 화면이 멈춰 있다」** 하나다. 오류도 콘솔도 없고
                 * 🔴 LIVE 배지는 떠 있으며 스트림도 계속 받는다 — 아무도 `play()` 를 부르지
                 * 않았을 뿐이다(2026-09-22 데모 실기 · 후크로 `play` 0건을 확인했다).
                 *
                 * **창이 이미 열린 채로 채널에 들어갈 때만 난다.** 컷을 무장해 들어가는 길
                 * (`armLiveCut` → 꼬리 스왑)은 `performSwap` 이 틀어 주므로 이 약속을 기다리지
                 * 않는다 — 하네스가 심은 이벤트를 **기다려서** 들어가는 판이라 열넷이 다 초록인
                 * 채로 이 길만 빠져 있었다.
                 *
                 * 아이폰 어댑터에는 같은 덫이 없다 — 거기에는 `FRAME_PROOF_GRACE_MS` 유예가 있다.
                 */
                if (b.live) {
                    settleStart(0, 0);
                    return;
                }
                /*
                 * ★★★ v33. **라이브에는 `currentTime` 을 걸지 않는다** (`docs/67` §7.6 ① ·
                 * 확인 ③). 라이브에서 0 은 `seekable.start` 로 눌리고 그것은 **창의 뒤끝**
                 * (≈18초 전)이다 — 증상은 오류가 아니라 **「라이브가 18초 늦다」** 하나여서
                 * 혼자 보면 알 수 없고 **두 기기를 나란히 놓아야** 보인다.
                 *
                 * 걸지 않으면 hls.js 의 기본 라이브 동기(끝에서 3세그먼트 = 6초)가 그대로
                 * 쓰인다 — v28 의 ONAIR 경로가 그렇게 돌고 있고 무대에서 증명됐다.
                 */
                b.el.currentTime = alignedStartSec;
                // 프레임 하나가 실제로 합성되는 것을 확인한다 — 보이지 않는 창에서는 안 오므로 유예를 둔다.
                grace = setTimeout(() => {
                    settleStart(b.el.currentTime, SEEK_SETTLE_FRAMES);
                }, FRAME_PROOF_GRACE_MS);
                requestVideoFrame(b.el, (_now, meta) => {
                    settleStart(meta.mediaTime, 0);
                });
            };
            b.el.addEventListener('loadeddata', onLoaded);
            hls.attachMedia(b.el);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => {
                hls.loadSource(url);
                // 이어보기면 그 지점 근처부터 받는다. hls.js 가 그 자리의 프래그먼트를 고르고,
                // 청크 경계로 내리는 것은 위 `onLoaded` 가 한다.
                hls.startLoad(startAtSec !== undefined && startAtSec > 0 ? startAtSec : 0);
            });
        });
    }
    /**
     * 버퍼의 프레임 체인. 마지막 프레임 시각을 계속 기록하고, 꼬리가 무장돼 있으면
     * **이 콜백 안에서** 프리롤과 스왑을 수행한다.
     *
     * 체인은 버퍼당 하나만 돈다. 꼬리용으로 별도 체인을 걸면 같은 프레임에 두 번 반응한다.
     */
    startWatch(b) {
        if (b.watching)
            return;
        b.watching = true;
        // **전역 `gen` 이 아니라 버퍼별 세대를 본다.** 근거는 `Buffer.watchGen` 주석.
        const gen = b.watchGen;
        const step = (now, meta) => {
            if (!b.watching || gen !== b.watchGen)
                return;
            b.frames += 1;
            b.lastFrameEdt = meta.expectedDisplayTime;
            b.lastFrameAt = now;
            const tail = this.tail;
            const isFront = this.frontBuf === b;
            if (tail && isFront && !this.swapping) {
                if (!tail.prerolled && meta.mediaTime >= tail.prerollAt) {
                    tail.prerolled = true;
                    tail.prerollStartedAt = performance.now();
                    this.startPreroll();
                    // 워치독은 **프리롤 이후에만** 무장한다. 영상 중간의 버퍼 언더런을 전환으로
                    // 오인하지 않기 위함이다. 꼬리 창 안에서는 대기 버퍼가 이미 합성 중이라
                    // 일찍 발화해도 싸다 — A 를 한두 프레임 자를 뿐 무갭이다.
                    this.armWatchdog(tail);
                }
                if (tail.swapAt !== null && meta.mediaTime >= tail.swapAt - 0.5 * FRAME_DURATION_SEC) {
                    // 이 콜백의 expectedDisplayTime 이 t_A_last 의 확정값이다. 사후 추정하지 않는다.
                    this.performSwap(tail.prerolled ? 'preroll' : 'no-preroll', 'natural', meta.expectedDisplayTime, null);
                    return;
                }
                if (tail.prerolled)
                    this.armWatchdog(tail);
            }
            requestVideoFrame(b.el, step);
        };
        requestVideoFrame(b.el, step);
    }
    /**
     * ★ v33-e. **프레임 없이 컷을 터뜨린다** (2026-09-23 · `docs/68` §2.0-g).
     *
     * 보이지 않는 창에서는 위 프레임 체인이 돌지 않아 무장한 컷이 영영 안 터졌다. 판정은
     * 순수 함수 하나(`cutDueWithoutFrames`)이고 **프레임이 살아 있으면 언제나 거짓**이라 보이는
     * 창의 경로는 한 줄도 바뀌지 않는다. `onSwapped` 에 돌려주는 컷 초는 무장할 때 구한
     * 청크 경계 그대로다(`performSwap` 이 `tail.cutAtSec` 을 넘긴다).
     */
    cutWithoutFrames(el) {
        const tail = this.tail;
        const front = this.frontBuf;
        if (tail === null || this.swapping || front.el !== el)
            return;
        const due = cutDueWithoutFrames({
            cutAtSec: tail.cutAtSec,
            currentTimeSec: el.currentTime,
            lastFrameAtMs: front.lastFrameAt,
            nowMs: performance.now(),
        });
        if (!due)
            return;
        this.performSwap('timeupdate', 'natural', front.lastFrameEdt, null);
    }
    /** 대기 버퍼를 **음소거한 채** 재생 시작. 이것이 S4 의 전부다. */
    startPreroll() {
        const back = this.backBuf;
        if (!back.ready || back.playing)
            return;
        const tail = this.tail;
        // v35. 되감을 자리를 적는다 — 근거는 `rewindPreroll`.
        if (tail !== null)
            tail.backStartAt = back.el.currentTime;
        back.el.muted = true;
        back.el
            .play()
            .then(() => {
            /*
             * ★ v35. **`play()` 가 늦게 풀린 사이 프리롤이 되감겼으면 다시 멈춘다.** 같은 꼬리가
             * 아직 서 있는데 `prerolled` 가 내려가 있으면 그것이다(`rewindPreroll`). 꼬리가 바뀌었으면
             * (스왑이 이미 일어났다) 이 버퍼는 앞으로 나간 것이라 그대로 둔다.
             */
            if (tail !== null && this.tail === tail && !tail.prerolled) {
                back.el.pause();
                return;
            }
            back.playing = true;
            this.startWatch(back);
        })
            .catch((e) => {
            // 프리롤 실패는 에러가 아니다. 스왑 루틴이 그 자리에서 play() 를 부른다 (S2 강등).
            back.playing = false;
            void e;
        });
    }
    armWatchdog(tail) {
        if (tail.watchdog !== null)
            clearTimeout(tail.watchdog);
        tail.watchdog = setTimeout(() => {
            if (this.tail !== tail || this.swapping)
                return;
            this.performSwap('watchdog', 'natural', this.frontBuf.lastFrameEdt, null);
        }, WATCHDOG_MS);
    }
    clearTail() {
        if (this.tail?.watchdog !== null && this.tail?.watchdog !== undefined) {
            clearTimeout(this.tail.watchdog);
        }
        this.tail = null;
    }
    /**
     * **경로와 무관하게 스왑은 이 루틴 하나다.** `play()` 가 여기 있는 것이 폴백의 실체다 —
     * 프리롤이 성공했으면 no-op(0ms)이고, 실패했으면 그 자리에서 S2(67ms)가 된다.
     * `play()` 를 프리롤에만 두면 실패 시 S1(150ms)으로 떨어진다.
     */
    performSwap(path, trigger, aLastEdt, requestedAt) {
        if (this.swapping)
            return false;
        this.swapping = true;
        const tail = this.tail;
        const prerollStartedAt = tail?.prerollStartedAt ?? null;
        this.clearTail();
        const oldFront = this.frontBuf;
        const backIndex = this.backIndex;
        const back = this.backBuf;
        const droppedBefore = droppedFrames(back.el);
        if (!back.playing) {
            back.el
                .play()
                .then(() => {
                back.playing = true;
            })
                .catch((e) => this.errorCb?.(e));
        }
        // 음소거 해제는 노출과 같은 태스크에서 한다. 프리롤 동안 소비된 앞부분은 화면도 같은 양이
        // 잘리므로 동기는 유지된다.
        back.el.muted = false;
        back.el.style.opacity = '1';
        back.el.style.zIndex = '2';
        oldFront.el.style.opacity = '0';
        oldFront.el.style.zIndex = '1';
        this.front = backIndex;
        const swapAt = performance.now();
        // **A 의 정리를 이 태스크에서 하지 않는다.** `hls.destroy()` 는 문제의 vsync 에
        // 메인 스레드를 물린다. 다음 태스크로 미룬다.
        //
        // 미루는 대가가 있다. 코어는 `onSwapped` 를 받은 즉시 다음 항목의 `prepareNext()` 를
        // 부르고(`docs/05-receiver-sdk.md` 「프리로드 시점」), 그 대상이 바로 이 `oldFront` 다.
        // 마이크로태스크가 타이머보다 먼저 도므로 **정리보다 재적재가 앞선다.** 그때 그냥
        // 파괴하면 방금 적재한 다음 항목을 지워, 다음 꼬리에서 `videoSpan` 이 없어
        // `armTailSwap` 이 포기하고 채널이 멈춘다 (실기에서 재현했다).
        // 그래서 **스왑 당시의 hls 인스턴스가 그대로일 때만** 파괴한다.
        //
        // ★ **그 값을 `onSwapped` 보다 먼저 잡는다** (v21/F13 실기가 찾았다). 뒤에서 잡으면
        // 「스왑 당시」가 아니라 **「코어가 손댄 뒤」**이고, 코어가 **동기로** 재적재하는
        // 경로에서는 그 둘이 다르다 — v21 의 `armAdNext()` 가 `await` 없이
        // `prepareNext()` 를 부르므로 여기 닿기 전에 이미 새 인스턴스가 들어와 있다.
        // 그러면 가드가 **자기가 지키려던 그 인스턴스를 파괴한다.**
        //
        // 증상이 pod 길이에 달려 있어 조용하다 — 광고가 **한 편이면 멀쩡하고**(브레이크가
        // 열릴 때 무장한 것이라 이 경로를 안 탄다) **두 편부터 복귀가 죽는다.** 광고 서버가
        // 그날 몇 편을 채웠는지에 따라 갈리므로 「가끔 광고 끝나고 화면이 멈춘다」로 보인다.
        const staleHls = oldFront.hls;
        // 코어에는 즉시 알린다 — `itemChanged` 가 화면 전환과 같은 프레임에 나가야
        // M6 이 오버레이를 제때 닫는다. 계측은 아래에서 비동기로 따라온다.
        tail?.opts.onSwapped(path, tail.cutAtSec);
        // **어댑터가 죽은 뒤 발화하면 안 된다** (v19). 엘리먼트를 입양하면 이 콜백이
        // **다음 어댑터가 쓰는 엘리먼트**를 멈추고 음소거한다 — `stop()` 이 `gen` 을 올리므로
        // 그 값을 잡아 두고 대조한다. `TASKS-V12` 가 「취소 불가」라 적어 이 안을 버렸던 자리다.
        const swapGen = this.gen;
        setTimeout(() => {
            if (swapGen !== this.gen)
                return;
            oldFront.el.pause();
            oldFront.el.muted = true;
            oldFront.playing = false;
            if (oldFront.hls === staleHls) {
                // `destroyHls` 가 `watching` 을 내리고 세대를 올린다 — A 의 체인은 여기서 끊긴다.
                this.destroyHls(oldFront);
            }
            this.startWatch(back);
            this.swapping = false;
        }, 0);
        // B 의 첫 표시 프레임으로 T_freeze 를 계산한다. M0 과 같은 정의다.
        requestVideoFrame(back.el, (_now, meta) => {
            const tBFirst = meta.expectedDisplayTime;
            const V = this.vsyncMs;
            const k = Math.round((FRAME_DURATION_SEC * 1000) / V);
            const raw = aLastEdt === null ? null : tBFirst - aLastEdt;
            this.transitionCb?.({
                trigger,
                path,
                tFreezeMs: raw === null ? null : Math.max(0, Math.round(raw / V) - k) * V,
                rawMs: raw,
                vsyncMs: V,
                prerollLeadMs: prerollStartedAt === null ? null : swapAt - prerollStartedAt,
                tSkipMs: requestedAt === null ? null : tBFirst - requestedAt,
                droppedB: droppedBefore === null ? null : (droppedFrames(back.el) ?? 0) - droppedBefore,
            });
        });
        return true;
    }
    reveal(index) {
        this.buffers.forEach((b, i) => {
            b.el.style.opacity = i === index ? '1' : '0';
            b.el.style.zIndex = i === index ? '2' : '1';
        });
        this.front = index;
    }
    /**
     * vsync 주기 실측. 60Hz 를 가정하지 않는다 — T_freeze 가 이 값으로 양자화되므로
     * 가정이 틀리면 수치 전체가 틀린다.
     */
    measureVsync() {
        return new Promise((resolve) => {
            const deltas = [];
            let last = 0;
            const step = (t) => {
                if (last !== 0)
                    deltas.push(t - last);
                last = t;
                if (deltas.length < 30) {
                    requestAnimationFrame(step);
                    return;
                }
                deltas.sort((a, b) => a - b);
                const median = deltas[Math.floor(deltas.length / 2)];
                if (median !== undefined && median > 1 && median < 100)
                    this.vsyncMs = median;
                resolve();
            };
            requestAnimationFrame(step);
        });
    }
}
/**
 * 마지막 비디오 프레임의 mediaTime.
 *
 *     videoStart + videoSpan − 1/30
 *
 * `duration` 을 쓰지 않는다. `duration` 은 비디오 끝과 오디오 끝 중 큰 쪽이라 비디오의 끝을
 * 대변하지 않는다 — 오디오가 비디오보다 16.67ms 넘게 긴 자산에서는 `duration` 기준 조건이
 * **한 번도 참이 되지 않아 전환 자체가 일어나지 않는다** (`docs/spike-result.md` 「부채 정산」 3절).
 */
function lastFrameMediaTime(b) {
    if (b.videoStart === null || b.videoSpan === null)
        return null;
    return b.videoStart + b.videoSpan - FRAME_DURATION_SEC;
}
/**
 * 프레임 콜백 발화는 **합성의 증거가 아니다** — 합성되지 않는 `<video>` 도 정상 발화한다(E0).
 * 그래서 버려진 프레임 수를 함께 본다.
 */
function droppedFrames(el) {
    if (typeof el.getVideoPlaybackQuality !== 'function')
        return null;
    return el.getVideoPlaybackQuality().droppedVideoFrames;
}
function requestVideoFrame(el, cb) {
    el.requestVideoFrameCallback(cb);
}
//# sourceMappingURL=hls-player.js.map